import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import {
  useCSCategoriesPaged,
  useCSCategoriesPagination,
  useCSCategoriesPageFilters,
  useCSCategoryFilterOptions,
  useCreateCSCategory,
  useUpdateCSCategory,
  useSetCSCategoryEnabled,
  useDeleteCSCategory
} from '@/hooks';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { SearchInput } from '@/components/ui/input';
import { TabsContent } from '@/components/ui/tabs';
import { PagePagination } from '@/components/shared';
import type { CSCategory } from '@/types';
import { ActionsCell, CSIntentFormDialog, CSIntentDeleteDialog } from '@/modules/cs-intents/components';
import {
  SETTINGS_HEADER_BUTTON_CLASS,
  SettingsPage,
  SettingsSelect,
  SettingsTableHead,
  SettingsTableRows,
  StatusTag,
  useTabParam,
  type SettingsColumn,
  type SettingsColumnHead,
  type SettingsTab
} from '@/modules/settings';
import { IntentsActiveTab } from './intents-active';
import { ACTIVE_INTENT_COLUMNS } from './intents-active-columns';

type IntentsTab = 'active' | 'glossary';

const TABS: SettingsTab<IntentsTab>[] = [
  { value: 'active', label: 'Active' },
  { value: 'glossary', label: 'Glossary' }
];

const ALL = 'all';
const SEARCH_CLASS = 'h-8 w-[240px] border-gray-500 bg-white';
const FILTER_TRIGGER_CLASS = 'h-8 w-[170px] rounded-full border border-gray-500 bg-white text-[12px]';

const GLOSSARY_COLUMNS: SettingsColumnHead[] = [
  { id: 'name', header: 'Intent Name', className: 'flex-[2]' },
  { id: 'definition', header: 'Description', className: 'flex-[3]' },
  { id: 'category', header: 'Category', className: 'flex-[2]' },
  { id: 'subcategory', header: 'Sub-category', className: 'flex-[2]' },
  { id: 'status', header: 'Status', className: 'w-[120px]' },
  { id: 'menu', header: null, className: 'w-[54px]' }
];

/** The customer-service intents of this brand: search, filter, add, edit, disable and delete. */
function GlossaryTab({ onEdit }: { onEdit: (intent: CSCategory) => void }) {
  const { data, isLoading } = useCSCategoriesPaged();
  const [{ page, limit }, setPagination] = useCSCategoriesPagination();
  const setEnabledMutation = useSetCSCategoryEnabled();
  const deleteMutation = useDeleteCSCategory();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleToggleEnabled = async (intent: CSCategory) => {
    const nextEnabled = !(intent.isEnabled !== false);
    try {
      await setEnabledMutation.mutateAsync({ id: intent.id, isEnabled: nextEnabled });
      toast.success(nextEnabled ? 'Intent enabled' : 'Intent disabled');
    } catch (err) {
      console.error('IntentsPage.handleToggleEnabled failed', { intentId: intent.id, nextEnabled, error: err });
      toast.error(getApiErrorMessage(err, 'Failed to update intent'));
    }
  };

  const cells: Record<string, (intent: CSCategory) => ReactNode> = {
    name: (intent) => (
      <div className='flex min-w-0 flex-col'>
        <span className='truncate'>{intent.name || '—'}</span>
        <span className='truncate font-mono text-[11px] text-gray-800'>{intent.id}</span>
      </div>
    ),
    definition: (intent) => (
      <span className='line-clamp-2 text-ink' title={intent.definition || undefined}>
        {intent.definition || '—'}
      </span>
    ),
    category: (intent) => <span className='truncate'>{intent.category || '—'}</span>,
    subcategory: (intent) => <span className='truncate'>{intent.subcategory || '—'}</span>,
    status: (intent) =>
      intent.isEnabled !== false ? <StatusTag tone='green'>Live</StatusTag> : <StatusTag tone='red'>Disabled</StatusTag>,
    menu: (intent) => (
      <ActionsCell intent={intent} onEdit={onEdit} onDelete={setDeletingId} onToggleEnabled={(i) => void handleToggleEnabled(i)} />
    )
  };
  const columns: SettingsColumn<CSCategory>[] = GLOSSARY_COLUMNS.map((head) => ({ ...head, cell: cells[head.id] }));

  return (
    <>
      <SettingsTableRows columns={columns} rows={data?.categories ?? []} rowKey={(intent) => intent.id} isLoading={isLoading} empty='No intents found.' />
      {(data?.total ?? 0) > 0 && (
        <div className='p-4'>
          <PagePagination
            noun='intent'
            total={data?.total ?? 0}
            page={page}
            limit={limit}
            totalPages={data?.totalPages ?? 1}
            onPageChange={(newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
            onLimitChange={(newLimit) => setPagination({ page: 1, limit: newLimit })}
          />
        </div>
      )}

      <CSIntentDeleteDialog
        deletingId={deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        isDeleting={deleteMutation.isPending}
        onDelete={(id) => deleteMutation.mutateAsync(id)}
        onDeleted={() => {}}
      />
    </>
  );
}

/** Search, category filters and Add for the Glossary, shown in the page header. */
function GlossaryTools({ onAdd }: { onAdd: () => void }) {
  const [filters, setFilters] = useCSCategoriesPageFilters();
  const [, setPagination] = useCSCategoriesPagination();
  const { data: filterOptions } = useCSCategoryFilterOptions();
  const [searchInput, setSearchInput] = useState(filters.name ?? '');
  const categoryOptions = useMemo(() => filterOptions?.categories ?? [], [filterOptions]);
  const subcategoryOptions = useMemo(() => filterOptions?.subcategories ?? [], [filterOptions]);

  // Debounce name search — reset to page 1 on change
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, name: searchInput || undefined }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (key: 'category' | 'subcategory', value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === ALL ? undefined : value,
      // A new category clears the sub-category picked under the old one.
      ...(key === 'category' && { subcategory: undefined })
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <>
      <SearchInput placeholder='Search' value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className={SEARCH_CLASS} />
      <SettingsSelect
        value={filters.category ?? ALL}
        onValueChange={(value) => setFilter('category', value)}
        options={[{ value: ALL, label: 'All categories' }, ...categoryOptions.map((cat) => ({ value: cat, label: cat }))]}
        aria-label='Category'
        className={FILTER_TRIGGER_CLASS}
      />
      <SettingsSelect
        value={filters.subcategory ?? ALL}
        onValueChange={(value) => setFilter('subcategory', value)}
        options={[{ value: ALL, label: 'All sub-categories' }, ...subcategoryOptions.map((sub) => ({ value: sub, label: sub }))]}
        aria-label='Sub-category'
        className={FILTER_TRIGGER_CLASS}
      />
      <button type='button' onClick={onAdd} className={cn(SETTINGS_HEADER_BUTTON_CLASS, 'border-text-quote bg-ink text-gray-50 hover:bg-ink-strong')}>
        <Plus className='size-3.5' />
        Add intent
      </button>
    </>
  );
}

/** Settings › Intents: Active (per-intent switches) and Glossary (the intents themselves). */
export default function IntentsPage() {
  const [tab, setTab] = useTabParam(TABS, 'active');
  const [activeSearch, setActiveSearch] = useState('');
  const createMutation = useCreateCSCategory();
  const updateMutation = useUpdateCSCategory();
  // Add and Edit share one sheet: null edits nothing, so it creates.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingIntent, setEditingIntent] = useState<CSCategory | null>(null);
  const openSheet = (intent: CSCategory | null) => {
    setEditingIntent(intent);
    setSheetOpen(true);
  };
  return (
    <SettingsPage
      title='Intents'
      tabs={TABS}
      tab={tab}
      onTabChange={setTab}
      tools={
        tab === 'active' ? (
          <SearchInput placeholder='Search intents' value={activeSearch} onChange={(e) => setActiveSearch(e.target.value)} className={SEARCH_CLASS} />
        ) : (
          <GlossaryTools onAdd={() => openSheet(null)} />
        )
      }
      subHeader={<SettingsTableHead columns={tab === 'active' ? ACTIVE_INTENT_COLUMNS : GLOSSARY_COLUMNS} />}
    >
      <TabsContent value='active' className='mt-0'>
        <IntentsActiveTab search={activeSearch} />
      </TabsContent>
      <TabsContent value='glossary' className='mt-0'>
        <GlossaryTab onEdit={openSheet} />
      </TabsContent>
      <CSIntentFormDialog
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        editingIntent={editingIntent}
        isSaving={createMutation.isPending || updateMutation.isPending}
        onCreate={(payload) => createMutation.mutateAsync(payload)}
        onUpdate={(id, payload) => updateMutation.mutateAsync({ id, data: payload })}
        onSaved={() => {}}
      />
    </SettingsPage>
  );
}
